import { useEffect, useMemo, useState } from "react";
import { Calculator, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";


function currency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);
}


function marginTone(margin, minimum) {
  if (margin >= minimum) {
    return "green";
  }
  if (margin >= minimum - 5) {
    return "yellow";
  }
  return "red";
}


function contractHourlyRate(contract) {
  if (contract.pricing_model === "hourly") {
    return Number(contract.bill_rate || 0);
  }
  const annualHours = Number(contract.staff_count || 0) * Number(contract.hours_per_week || 0) * Number(contract.contract_weeks || 0);
  return annualHours ? Number(contract.annual_contract_value || 0) / annualHours : 0;
}


export default function PayRateCalculatorPanel({ canManage, contracts = [], standalone = false }) {
  const [settings, setSettings] = useState(null);
  const [billRate, setBillRate] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedContractId, setSelectedContractId] = useState("");

  const reviewedContracts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const current = (contract) => contract.is_current !== false && contract.client_active !== false && (!contract.contract_end_date || contract.contract_end_date.slice(0, 10) >= today);
    return contracts.filter((contract) => contract.status === "Linked" && contractHourlyRate(contract) > 0 && current(contract));
  }, [contracts]);

  async function loadSettings() {
    try {
      const response = await api.get("/pay-rate-calculator/settings");
      setSettings(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  useEffect(() => {
    loadSettings();
  }, []);

  function selectContract(contractId) {
    setSelectedContractId(contractId);
    const selected = reviewedContracts.find((contract) => contract.id === contractId);
    setBillRate(selected ? String(contractHourlyRate(selected).toFixed(2)) : "");
    setResult(null);
  }

  async function calculate(event) {
    event.preventDefault();
    try {
      const response = await api.post("/pay-rate-calculator/calculate", {
        client_bill_rate: Number(billRate),
      });
      setResult(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function saveSettings() {
    try {
      const response = await api.put("/pay-rate-calculator/settings", settings);
      setSettings(response.data);
      setNotice("Margin assumptions saved for internal calculations.");
      setError("");
      if (billRate) {
        const calculation = await api.post("/pay-rate-calculator/calculate", {
          client_bill_rate: Number(billRate),
        });
        setResult(calculation.data);
      }
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  if (!settings && !error) {
    return <section className="pay-rate-panel pay-rate-loading" data-testid="pay-rate-calculator-loading">Loading pay rate settings…</section>;
  }

  if (!settings) {
    return <section className="pay-rate-panel pay-rate-error" data-testid="pay-rate-calculator-error">{error}</section>;
  }

  return (
    <section className="pay-rate-panel" data-testid="pay-rate-calculator-panel">
      <div className="pay-rate-heading">
        <div>
          <p className="eyebrow">MARGIN PROTECTION</p>
          <h2>Pay rate calculator</h2>
          <p>Set offers from the client bill rate while protecting a {settings.minimum_margin_percent}% minimum margin.</p>
        </div>
        <Calculator size={22} aria-hidden="true" />
      </div>
      {error && <p className="form-message error-message" data-testid="pay-rate-error-message">{error}</p>}
      {notice && <p className="form-message success-message" data-testid="pay-rate-success-message">{notice}</p>}
      <form className="pay-rate-form" data-testid="pay-rate-calculator-form" onSubmit={calculate}>
        {standalone && <label>Reviewed contract<select data-testid="pay-rate-contract-select" value={selectedContractId} onChange={(event) => selectContract(event.target.value)} required><option value="">Select a current contract</option>{reviewedContracts.map((contract) => <option key={contract.id} value={contract.id}>{contract.contract_name} · {contract.client_name}</option>)}</select></label>}
        <label>Client bill rate / hour<input data-testid="client-bill-rate-input" min="0.01" step="0.01" type="number" value={billRate} onChange={(event) => setBillRate(event.target.value)} readOnly={standalone && !canManage} required /></label>
        <button className="button button-primary" data-testid="calculate-pay-rate-button"><Calculator size={16} /> Calculate offers</button>
      </form>
      {canManage && <div className="pay-rate-settings" data-testid="pay-rate-settings"><label>W-2 burden %<input data-testid="w2-burden-input" min="0" max="90" step="0.1" type="number" value={settings.w2_burden_percent} onChange={(event) => setSettings({ ...settings, w2_burden_percent: event.target.value })} /></label><label>1099 overhead %<input data-testid="contractor-overhead-input" min="0" max="90" step="0.1" type="number" value={settings.contractor_overhead_percent} onChange={(event) => setSettings({ ...settings, contractor_overhead_percent: event.target.value })} /></label><label>Minimum margin %<input data-testid="minimum-margin-input" min="1" max="90" step="0.1" type="number" value={settings.minimum_margin_percent} onChange={(event) => setSettings({ ...settings, minimum_margin_percent: event.target.value })} /></label><button className="button button-secondary" data-testid="save-pay-rate-settings-button" onClick={saveSettings} type="button"><Save size={15} /> Save defaults</button></div>}
      {result && <div className="pay-rate-results" data-testid="pay-rate-results"><article><small>Maximum 1099 offer</small><strong data-testid="maximum-1099-rate">{currency(result.maximum_1099_rate)}/hr</strong><span>Total cost {currency(result.contractor_total_cost)} · {result.contractor_overhead_percent}% overhead</span></article><article><small>Maximum W-2 base</small><strong data-testid="maximum-w2-rate">{currency(result.maximum_w2_base_rate)}/hr</strong><span>Total cost {currency(result.w2_total_cost)} · {result.w2_burden_percent}% burden</span></article><article className={`pay-rate-margin pay-rate-${marginTone(result.w2_margin_percent, result.minimum_margin_percent)}`}><small>Protected gross margin</small><strong data-testid="pay-rate-margin-percent">{result.minimum_margin_percent}%</strong><span>Up to {currency(result.maximum_total_cost)}/hr total labor cost</span></article></div>}
    </section>
  );
}